import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import { markInvalidFields } from "@/validation/markInvalidFields";

/**
 * SCRUM-2217: the Add Category form state, written as a createSlice like every
 * reducer in this client (SCRUM-1954) and mounted LOCALLY with
 * `useReducer(addCategoryForm.reducer, ...)` — it is the unsaved draft of the
 * form, not store data. The SCRUM-2127 pattern of the Update screen.
 */

// SCRUM-1705: the node's permanent identity (SCRUM-1673: `^[a-z0-9_]+$`, max 64,
// immutable). Pre-filled from the name until the editor edits it by hand, so
// the usual case costs no extra typing; the server refuses anything else.
export const CODE_PATTERN = /^[a-z0-9_]+$/;
export const codeFromName = (name: string) =>
  String(name || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 64);

/** A react-select option value for the parent; "None" carries `_id: 0`. */
export type ParentValue = { _id: string | number; name: string; slug: string };

type Validation = {
  name: boolean;
  slug: boolean;
  code: boolean;
  codeMessage: string;
  parentcategorie: boolean | null;
};

const initialValidation: Validation = {
  name: true,
  slug: true,
  code: true,
  codeMessage: "",
  parentcategorie: null,
};

type AddCategoryFormState = {
  parent: ParentValue | "";
  name: string;
  slug: string;
  code: string;
  codeTouched: boolean;
  // SCRUM-1668: the trunk flag (SCRUM-1413 D1). Off by default — a new node is
  // a listing surface until an editor says product URLs live under it.
  hostsProducts: boolean;
  /** the property ids switched on in the table */
  selectedPropertyIds: string[];
  validation: Validation;
};

const initialAddCategoryFormState: AddCategoryFormState = {
  parent: "",
  name: "",
  slug: "",
  code: "",
  codeTouched: false,
  hostsProducts: false,
  selectedPropertyIds: [],
  validation: initialValidation,
};

export const addCategoryForm = createSlice({
  name: "addCategoryForm",
  initialState: initialAddCategoryFormState,
  reducers: {
    parentSelected(state, action: PayloadAction<ParentValue | "">) {
      state.parent = action.payload;
      state.validation.parentcategorie = action.payload === "";
    },

    nameChanged(state, action: PayloadAction<string>) {
      state.name = action.payload;
      state.validation.name = true;
      // SCRUM-1705: follow the name until the editor takes the code over.
      if (!state.codeTouched) {
        state.code = codeFromName(action.payload);
        state.validation.code = true;
        state.validation.codeMessage = "";
      }
    },

    codeChanged(state, action: PayloadAction<string>) {
      state.code = String(action.payload || "").toLowerCase();
      state.codeTouched = true;
      state.validation.code = true;
      state.validation.codeMessage = "";
    },

    slugChanged(state, action: PayloadAction<string>) {
      state.slug = action.payload;
      state.validation.slug = true;
    },

    hostsProductsChanged(state, action: PayloadAction<boolean>) {
      state.hostsProducts = action.payload;
    },

    propertyToggled(state, action: PayloadAction<string>) {
      const id = action.payload;
      state.selectedPropertyIds = state.selectedPropertyIds.includes(id)
        ? state.selectedPropertyIds.filter((x) => x !== id)
        : [...state.selectedPropertyIds, id];
    },

    parentMissing(state) {
      state.validation.parentcategorie = true;
    },

    codeRejected(state) {
      state.validation.code = false;
      state.validation.codeMessage = "Required: lower-case letters, digits and underscores (max 64).";
    },

    serverErrorsReceived(
      state,
      action: PayloadAction<Array<{ param: string; value: unknown; msg?: string }>>
    ) {
      const next = markInvalidFields(state.validation, action.payload);
      // SCRUM-1705: the route's own message for `code` (required / format).
      for (const e of action.payload) {
        if (e?.param === "code") {
          next.code = false;
          next.codeMessage = e.msg || "";
        }
      }
      state.validation = next;
    },

    // SCRUM-1705: the 409 body of both create routes (SCRUM-1673) names the node
    // that already carries the code.
    duplicateCode(state, action: PayloadAction<string>) {
      state.validation.code = false;
      state.validation.codeMessage = `Code is already used by another node${action.payload}.`;
    },

    // A successful add resets the draft; the parent and the trunk flag stay, as they always did.
    saved(state) {
      state.validation = initialValidation;
      state.name = "";
      state.slug = "";
      state.code = "";
      state.codeTouched = false;
      state.selectedPropertyIds = [];
    },
  },
});
